import { useMemo } from 'react';
import { IconUser } from '@tabler/icons-react';
import styled from 'styled-components';

import { GetSiteSetupsBySiteIdQuery, useGetSiteSetupsBySiteIdQuery } from 'data/__generated__';
import { Chronology, ChronologyEntry } from 'components/DetailsView/Chronology';
import { SectionContainer } from 'components/DetailsView/Section';
import { DynamicDeliveryTag } from 'components/DynamicDeliveryTag';
import { Loader } from 'components/Loader';
import { colors } from 'constants/colors';
import { SiteSetupTrelloItem } from '../components/SiteSetupTrelloItem';
import { SETUP_STATE_COLORS, SITE_SETUP_STATES_LABELS } from '../constants/states';

const SECTION_TITLE = 'Chronologie des rangements du site';

type SetupItem = GetSiteSetupsBySiteIdQuery['getSiteSetupsBySiteId'][number];

export const OtherSiteSetupSection = ({ siteSetup }: { siteSetup: { siteId: string; date: string; _id: string } }) => {
    const {
        data: otherSiteSetupsData,
        loading: otherSiteSetupsLoading,
        error: otherSiteSetupsError,
    } = useGetSiteSetupsBySiteIdQuery({
        variables: {
            siteId: siteSetup.siteId,
            date: siteSetup.date,
        },
    });

    if (otherSiteSetupsLoading) {
        return (
            <SectionContainer title={SECTION_TITLE} isInitiallyOpen>
                <Loader />
            </SectionContainer>
        );
    }

    if (otherSiteSetupsError || !otherSiteSetupsData) {
        return (
            <SectionContainer title={SECTION_TITLE} isInitiallyOpen>
                <ErrorText>Erreur lors du chargement des autres rangements</ErrorText>
            </SectionContainer>
        );
    }

    const setups = otherSiteSetupsData.getSiteSetupsBySiteId;
    const hasOtherSetups = setups.some((setup) => setup._id !== siteSetup._id);

    if (!hasOtherSetups) {
        return (
            <SectionContainer title={SECTION_TITLE} isInitiallyOpen>
                <NoDataText>Aucun autre rangement trouvé pour ce site</NoDataText>
            </SectionContainer>
        );
    }

    return (
        <SectionContainer title={SECTION_TITLE} isInitiallyOpen>
            <SetupChronology setups={setups} currentSetupId={siteSetup._id} />
        </SectionContainer>
    );
};

const SetupChronology = ({ setups, currentSetupId }: { setups: SetupItem[]; currentSetupId: string }) => {
    const entries = useMemo<ChronologyEntry[]>(
        () =>
            setups.map((setup) => ({
                id: setup._id,
                date: setup.date,
                color: setup.isFinishForced ? colors.red : SETUP_STATE_COLORS[setup.state],
                stateLabel: setup.isFinishForced ? 'Finition forcée' : SITE_SETUP_STATES_LABELS[setup.state],
                meta: setup.deliveryPerson ? (
                    <DeliveryPerson>
                        <IconUser size={14} />
                        {setup.deliveryPerson.fullname}
                    </DeliveryPerson>
                ) : null,
            })),
        [setups],
    );

    return (
        <Chronology
            entries={entries}
            currentEntryId={currentSetupId}
            renderEntryDetails={({ id }) => {
                const selectedSetup = setups.find((setup) => setup._id === id);
                return selectedSetup ? <SiteSetupTrelloItem siteSetup={selectedSetup} showDate /> : null;
            }}
        />
    );
};

const DeliveryPerson = styled.span`
    display: flex;
    align-items: center;
    gap: 3px;
`;

const ErrorText = styled.div`
    color: ${({ theme }) => theme.errorColor};
    text-align: center;
    padding: 20px;
`;

const NoDataText = styled.div`
    color: ${({ theme }) => theme.textColor};
    text-align: center;
    padding: 20px;
    font-style: italic;
`;
